import { styled } from '@mui/material/styles'
import Tooltip from '@mui/material/Tooltip'
import { has, isNil, isPlainObject } from 'lodash'
import React from 'react'

import Colors from '#/styles/Colors'
import LineClampStyle from '#/styles/LineClamp'

interface WrapperProps {
  bold?: boolean
  italic?: boolean
  level?: string | null
}

const Wrapper = styled('div', {
  shouldForwardProp: prop => !['bold', 'italic', 'level'].includes(prop as string),
})<WrapperProps>(({ bold, italic, level }) => ({
  alignItems: 'center',
  color: Colors.feedback[level as keyof typeof Colors.feedback]?.color,
  display: 'flex',
  fontStyle: italic ? 'italic' : 'normal',
  fontWeight: bold ? 700 : 400,
  gap: 5,
}))

export interface TypeStringProps {
  /** Texto em negrito. */
  bold?: boolean
  /** Estilo inline aplicado ao container. */
  customStyle?: React.CSSProperties
  /** Texto em itálico. */
  italic?: boolean
  /** Nível semântico (chave de `Colors.feedback`) que define a cor do texto. */
  level?: string | null
  /** Conteúdo exibido antes do valor. */
  prefix?: React.ReactNode
  /** Conteúdo exibido depois do valor. */
  suffix?: React.ReactNode
  /** Texto do tooltip; quando omitido, usa o próprio `value`. */
  tooltip?: React.ReactNode
  /** Valor a ser exibido; quando objeto, exibe a chave text */
  value?: React.ReactNode | { text: React.ReactNode }
}

const TypeString = ({
  bold,
  customStyle = {},
  italic,
  level,
  prefix,
  suffix,
  tooltip,
  value,
}: TypeStringProps) => {
  const displayValue =
    isPlainObject(value) && has(value, 'text') ? (value as { text: React.ReactNode }).text : value

  if (isNil(displayValue)) {
    return '-'
  }

  if (
    typeof displayValue !== 'string' &&
    typeof displayValue !== 'number' &&
    !React.isValidElement(displayValue)
  ) {
    console.warn('Error: Column string with object')
    return '-'
  }

  return (
    <Tooltip title={tooltip ?? displayValue}>
      <Wrapper bold={bold} italic={italic} level={level} style={customStyle}>
        {prefix && <span>{prefix}</span>}
        <div style={{ ...LineClampStyle, width: 'max-content' } as React.CSSProperties}>
          {displayValue}
        </div>
        {suffix && <span>{suffix}</span>}
      </Wrapper>
    </Tooltip>
  )
}

export default TypeString
